(() => {
  const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const sliders = new WeakMap();

  if (!customElements.get('sticky-header')) {
    class StraferzStickyHeader extends HTMLElement {
      constructor() {
        super();
        this.setHeaderHeight = this.setHeaderHeight.bind(this);
      }

      connectedCallback() {
        this.header = document.querySelector('.section-header');
        if (!this.header) return;
        this.mobileMediaQuery = window.matchMedia('(max-width: 990px)');
        this.headerIsAlwaysSticky = ['always', 'reduce-logo-size'].includes(this.dataset.stickyType);
        this.headerBounds = {};
        this.setHeaderHeight();
        this.mobileMediaQuery.addEventListener('change', this.setHeaderHeight);
        if (this.headerIsAlwaysSticky) this.header.classList.add('shopify-section-header-sticky');
        this.currentScrollTop = 0;
        this.preventReveal = false;
        this.predictiveSearches = this.querySelectorAll('predictive-search');
        this.onScrollHandler = this.onScroll.bind(this);
        this.hideHeaderOnScrollUp = () => (this.preventReveal = true);
        this.addEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
        window.addEventListener('scroll', this.onScrollHandler, false);
        this.createObserver();
      }

      setHeaderHeight() {
        document.documentElement.style.setProperty('--header-height', `${this.header.offsetHeight}px`);
      }

      disconnectedCallback() {
        this.mobileMediaQuery?.removeEventListener('change', this.setHeaderHeight);
        this.removeEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
        window.removeEventListener('scroll', this.onScrollHandler);
      }

      createObserver() {
        if (!('IntersectionObserver' in window)) return;
        const observer = new IntersectionObserver((entries) => {
          this.headerBounds = entries[0].intersectionRect;
          observer.disconnect();
        });
        observer.observe(this.header);
      }

      onScroll() {
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        if ([...this.predictiveSearches].some((search) => search.isOpen)) return;
        if (scrollTop > this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
          this.header.classList.add('scrolled-past-header');
          if (!this.preventReveal) requestAnimationFrame(() => this.hide());
        } else if (scrollTop < this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
          this.header.classList.add('scrolled-past-header');
          if (!this.preventReveal) requestAnimationFrame(() => this.reveal());
          else requestAnimationFrame(() => this.hide());
        } else if (scrollTop <= this.headerBounds.top) {
          this.header.classList.remove('scrolled-past-header');
          requestAnimationFrame(() => this.reset());
        }
        this.currentScrollTop = scrollTop;
      }

      hide() {
        if (this.headerIsAlwaysSticky) return;
        this.header.classList.add('shopify-section-header-hidden', 'shopify-section-header-sticky');
        this.closeMenuDisclosure();
        this.closeSearchModal();
      }

      reveal() {
        if (this.headerIsAlwaysSticky) return;
        this.header.classList.add('shopify-section-header-sticky', 'animate');
        this.header.classList.remove('shopify-section-header-hidden');
      }

      reset() {
        if (this.headerIsAlwaysSticky) return;
        this.header.classList.remove('shopify-section-header-hidden', 'shopify-section-header-sticky', 'animate');
      }

      closeMenuDisclosure() {
        this.disclosures = this.disclosures || this.header.querySelectorAll('header-menu');
        this.disclosures.forEach((disclosure) => disclosure.close());
      }

      closeSearchModal() {
        this.searchModals = this.searchModals || this.header.querySelectorAll('details-modal');
        this.searchModals.forEach((searchModal) => searchModal.close(false));
      }
    }

    customElements.define('sticky-header', StraferzStickyHeader);
  }

  const cleanHeader = (root) => {
    root.querySelectorAll('.header__inline-menu li, .menu-drawer__navigation li').forEach((item) => {
      const trigger = item.querySelector(':scope > a, :scope > details > summary');
      if (!trigger) return;
      const label = trigger.textContent.replace(/\s+/g, ' ').trim();
      const href = trigger.matches('a') ? trigger.getAttribute('href')?.trim() : null;
      if (!label || href === '') item.classList.add('is-straferz-hidden');
    });

    root.querySelectorAll('.header__inline-menu li, .menu-drawer__navigation li').forEach((item) => {
      const submenu = item.querySelector(':scope > details');
      if (!submenu) return;
      const hasVisibleChild = [...submenu.querySelectorAll(':scope .header__submenu > li, :scope .menu-drawer__submenu > .menu-drawer__inner-submenu > ul > li')]
        .some((child) => !child.classList.contains('is-straferz-hidden'));
      if (!hasVisibleChild) item.classList.add('is-straferz-hidden');
    });
  };

  const initializeSlider = (slider) => {
    if (sliders.has(slider)) return;

    const slides = [...slider.querySelectorAll('[data-slide]')];
    if (!slides.length) return;

    const dots = [...slider.querySelectorAll('[data-dot]')];
    const interval = Number(slider.dataset.interval) || 7000;
    let index = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
    let timer = null;
    let pointerStart = null;
    let hovered = false;
    let focused = false;
    let visible = true;

    const setSlide = (nextIndex) => {
      index = (nextIndex + slides.length) % slides.length;
      slides.forEach((slide, slideIndex) => {
        const active = slideIndex === index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
        slide.inert = !active;
      });
      dots.forEach((dot, dotIndex) => {
        dot.setAttribute('aria-selected', dotIndex === index ? 'true' : 'false');
      });
    };

    const stop = () => {
      if (timer) window.clearInterval(timer);
      timer = null;
    };

    const start = () => {
      stop();
      if (slides.length < 2 || slider.dataset.autoplay !== 'true' || reducedMotionQuery.matches || hovered || focused || !visible) return;
      timer = window.setInterval(() => setSlide(index + 1), interval);
    };

    const previous = slider.querySelector('[data-prev]');
    const next = slider.querySelector('[data-next]');
    const onPrevious = () => { setSlide(index - 1); start(); };
    const onNext = () => { setSlide(index + 1); start(); };
    const onEnter = () => { hovered = true; stop(); };
    const onLeave = () => { hovered = false; start(); };
    const onFocus = () => { focused = true; stop(); };
    const onBlur = () => { focused = false; start(); };
    const onPointerDown = (event) => { pointerStart = event.clientX; };
    const onPointerUp = (event) => {
      if (pointerStart === null) return;
      const distance = event.clientX - pointerStart;
      pointerStart = null;
      if (Math.abs(distance) < 40) return;
      if (distance > 0) onPrevious(); else onNext();
    };
    const onVisibility = () => { visible = document.visibilityState !== 'hidden'; if (visible) start(); else stop(); };
    const onReducedMotion = () => { if (reducedMotionQuery.matches) stop(); else start(); };
    const intersectionObserver = 'IntersectionObserver' in window
      ? new IntersectionObserver((entries) => {
          visible = entries[0]?.isIntersecting !== false;
          if (visible) start(); else stop();
        }, { threshold: 0.05 })
      : null;

    setSlide(index);
    previous?.addEventListener('click', onPrevious);
    next?.addEventListener('click', onNext);
    dots.forEach((dot) => dot.addEventListener('click', () => { setSlide(Number(dot.dataset.dot) || 0); start(); }));
    slider.addEventListener('mouseenter', onEnter);
    slider.addEventListener('mouseleave', onLeave);
    slider.addEventListener('focusin', onFocus);
    slider.addEventListener('focusout', onBlur);
    slider.addEventListener('pointerdown', onPointerDown, { passive: true });
    slider.addEventListener('pointerup', onPointerUp, { passive: true });
    document.addEventListener('visibilitychange', onVisibility);
    reducedMotionQuery.addEventListener?.('change', onReducedMotion);
    intersectionObserver?.observe(slider);

    const cleanup = () => {
      stop();
      previous?.removeEventListener('click', onPrevious);
      next?.removeEventListener('click', onNext);
      slider.removeEventListener('mouseenter', onEnter);
      slider.removeEventListener('mouseleave', onLeave);
      slider.removeEventListener('focusin', onFocus);
      slider.removeEventListener('focusout', onBlur);
      slider.removeEventListener('pointerdown', onPointerDown);
      slider.removeEventListener('pointerup', onPointerUp);
      document.removeEventListener('visibilitychange', onVisibility);
      reducedMotionQuery.removeEventListener?.('change', onReducedMotion);
      intersectionObserver?.disconnect();
      sliders.delete(slider);
    };

    sliders.set(slider, cleanup);
    start();
  };

  const initializeFaq = (root) => {
    root.querySelectorAll('details').forEach((details) => {
      if (details.dataset.straferzFaqReady === 'true') return;
      details.dataset.straferzFaqReady = 'true';
      const summary = details.querySelector(':scope > summary');
      const answer = details.querySelector(':scope > .straferz-faq__answer');
      if (!summary || !answer) return;

      summary.addEventListener('click', (event) => {
        event.preventDefault();
        if (reducedMotionQuery.matches) {
          details.open = !details.open;
          return;
        }

        if (details.open) {
          const startHeight = answer.offsetHeight;
          answer.style.overflow = 'hidden';
          answer.style.height = `${startHeight}px`;
          requestAnimationFrame(() => {
            answer.style.transition = 'height .24s ease';
            answer.style.height = '0px';
            answer.addEventListener('transitionend', () => {
              details.open = false;
              answer.style.removeProperty('height');
              answer.style.removeProperty('overflow');
              answer.style.removeProperty('transition');
            }, { once: true });
          });
          return;
        }

        details.open = true;
        answer.style.overflow = 'hidden';
        answer.style.height = '0px';
        requestAnimationFrame(() => {
          answer.style.transition = 'height .24s ease';
          answer.style.height = `${answer.scrollHeight}px`;
          answer.addEventListener('transitionend', () => {
            answer.style.removeProperty('height');
            answer.style.removeProperty('overflow');
            answer.style.removeProperty('transition');
          }, { once: true });
        });
      });
    });
  };

  const select = (scope, selector) => {
    const matches = [];
    if (scope.matches?.(selector)) matches.push(scope);
    matches.push(...(scope.querySelectorAll?.(selector) || []));
    return matches;
  };

  const initialize = (scope = document) => {
    select(scope, '.straferz-shell').forEach(cleanHeader);
    select(scope, '[data-straferz-slider]').forEach(initializeSlider);
    select(scope, '.section-straferz-faq').forEach(initializeFaq);
  };

  document.addEventListener('DOMContentLoaded', () => initialize());
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll?.('[data-straferz-slider]').forEach((slider) => sliders.get(slider)?.());
  });
  initialize();
})();
